import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Upload,
  Download,
  FileSpreadsheet,
  Check,
  X,
  AlertCircle,
  Phone,
  User,
  Shield,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

export function BulkContactsUpdate() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  // Download template with current data
  const downloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await axios.get(`${API}/api/lettings/bulk-update/contacts/template`, { headers });
      const { columns, properties } = response.data;

      // Create CSV content
      let csv = columns.join(",") + "\n";
      properties.forEach(prop => {
        const row = columns.map(col => {
          const val = prop[col] || "";
          // Escape quotes and wrap in quotes if contains comma
          if (val.toString().includes(",") || val.toString().includes('"')) {
            return `"${val.toString().replace(/"/g, '""')}"`;
          }
          return val;
        });
        csv += row.join(",") + "\n";
      });

      // Download
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `property_contacts_template_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success(`Downloaded template with ${properties.length} properties`);
    } catch (error) {
      toast.error("Failed to download template");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(null);
      setResult(null);
    }
  };

  // Preview the file
  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${API}/api/lettings/bulk-update/contacts/preview`,
        formData,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );

      setPreview(response.data);
      toast.success(`Found ${response.data.total_rows} rows to process`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview file");
    } finally {
      setLoading(false);
    }
  };

  // Execute the update
  const handleExecute = async () => {
    if (!file) return;
    setExecuting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${API}/api/lettings/bulk-update/contacts/execute`,
        formData,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );

      setResult(response.data);
      toast.success(`Updated ${response.data.stats.properties_updated} properties!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to execute update");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="container max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Bulk Update Contacts & Compliance</h1>
          <p className="text-muted-foreground">
            Upload a CSV or Excel file to update landlord/tenant contacts and compliance dates
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Instructions */}
        <div className="space-y-6">
          {/* Step 1: Download Template */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">1</div>
                Download Template
              </CardTitle>
              <CardDescription>
                Get a CSV with all your properties pre-filled
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={downloadTemplate}
                disabled={downloadingTemplate}
                className="w-full"
                variant="outline"
              >
                {downloadingTemplate ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download Template CSV
              </Button>
            </CardContent>
          </Card>

          {/* Step 2: Fill in Data */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">2</div>
                Fill in Missing Data
              </CardTitle>
              <CardDescription>
                Add contact numbers and compliance dates
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-0.5 text-purple-500" />
                <div>
                  <p className="font-medium">Landlord Contacts</p>
                  <p className="text-muted-foreground text-xs">landlord_mobile, landlord_phone, landlord_email</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <User className="w-4 h-4 mt-0.5 text-blue-500" />
                <div>
                  <p className="font-medium">Tenant Contacts</p>
                  <p className="text-muted-foreground text-xs">tenant_mobile, tenant_phone, tenant_email</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 mt-0.5 text-green-500" />
                <div>
                  <p className="font-medium">Compliance Dates</p>
                  <p className="text-muted-foreground text-xs">gas_safety_expiry, epc_expiry_date, eicr_expiry</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Step 3: Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">3</div>
                Upload & Preview
              </CardTitle>
              <CardDescription>
                Upload your completed file to preview changes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  data-testid="file-upload-input"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <FileSpreadsheet className="w-10 h-10 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {file ? file.name : "Click to select file"}
                  </span>
                  <span className="text-xs text-muted-foreground">CSV or Excel</span>
                </label>
              </div>

              {file && (
                <Button
                  onClick={handlePreview}
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Preview Changes
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Preview & Results */}
        <div className="lg:col-span-2 space-y-6">
          {/* Preview Results */}
          {preview && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Preview Results</span>
                  <Badge variant="outline">{preview.total_rows} rows</Badge>
                </CardTitle>
                <CardDescription>
                  Review matched properties before updating
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Detected Fields */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="text-xs text-purple-600 font-medium">Landlord Fields</p>
                    <p className="text-lg font-bold text-purple-700">
                      {preview.detected_fields.contact_fields?.filter(f => f.includes('landlord')).length || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600 font-medium">Tenant Fields</p>
                    <p className="text-lg font-bold text-blue-700">
                      {preview.detected_fields.contact_fields?.filter(f => f.includes('tenant')).length || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-green-600 font-medium">Compliance Fields</p>
                    <p className="text-lg font-bold text-green-700">
                      {preview.detected_fields.compliance_fields?.length || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-600 font-medium">Access Fields</p>
                    <p className="text-lg font-bold text-amber-700">
                      {preview.detected_fields.access_fields?.length || 0}
                    </p>
                  </div>
                </div>

                {/* Preview Table */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Row</TableHead>
                        <TableHead>Match Value</TableHead>
                        <TableHead>Property Found</TableHead>
                        <TableHead>Updates</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.preview?.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-xs">{row.row_number}</TableCell>
                          <TableCell className="max-w-[150px] truncate text-sm">
                            {row.match_value}
                          </TableCell>
                          <TableCell>
                            {row.property_found ? (
                              <div className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-green-500" />
                                <span className="text-xs truncate max-w-[150px]">
                                  {row.property_address}
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-red-500">
                                <X className="w-4 h-4" />
                                <span className="text-xs">Not found</span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {Object.keys(row.updates || {}).slice(0, 3).map(field => (
                                <Badge key={field} variant="secondary" className="text-xs">
                                  {field.replace(/_/g, ' ')}
                                </Badge>
                              ))}
                              {Object.keys(row.updates || {}).length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{Object.keys(row.updates).length - 3} more
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Execute Button */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <AlertCircle className="w-4 h-4" />
                    <span>This will update {preview.preview?.filter(r => r.property_found).length || 0} properties</span>
                  </div>
                  <Button
                    onClick={handleExecute}
                    disabled={executing}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="execute-update-btn"
                  >
                    {executing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Execute Update
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Results */}
          {result && (
            <Card className="border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-lg text-green-700 flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Update Complete
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-white rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{result.stats.properties_updated}</p>
                    <p className="text-sm text-muted-foreground">Properties Updated</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{result.stats.properties_matched}</p>
                    <p className="text-sm text-muted-foreground">Properties Matched</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-600">{result.stats.properties_not_found}</p>
                    <p className="text-sm text-muted-foreground">Not Found</p>
                  </div>
                </div>

                {/* Fields Updated */}
                {result.stats.fields_updated && Object.keys(result.stats.fields_updated).length > 0 && (
                  <div className="p-4 bg-white rounded-lg">
                    <p className="text-sm font-medium mb-2">Fields Updated:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(result.stats.fields_updated).map(([field, count]) => (
                        <Badge key={field} variant="outline">
                          {field.replace(/_/g, ' ')}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Errors */}
                {result.stats.errors && result.stats.errors.length > 0 && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm font-medium text-amber-700 mb-2">Warnings ({result.stats.errors.length}):</p>
                    <div className="max-h-32 overflow-y-auto text-xs text-amber-600 space-y-1">
                      {result.stats.errors.slice(0, 10).map((err, idx) => (
                        <p key={idx}>{err}</p>
                      ))}
                      {result.stats.errors.length > 10 && (
                        <p>...and {result.stats.errors.length - 10} more</p>
                      )}
                    </div>
                  </div>
                )}

                <Button
                  variant="outline"
                  onClick={() => {
                    setFile(null);
                    setPreview(null);
                    setResult(null);
                  }}
                  className="w-full"
                >
                  Upload Another File
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Empty State */}
          {!preview && !result && (
            <Card className="border-dashed">
              <CardContent className="py-16 text-center">
                <FileSpreadsheet className="w-16 h-16 mx-auto text-muted-foreground opacity-30 mb-4" />
                <h3 className="text-lg font-medium mb-2">No File Selected</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Download the template, fill in the landlord mobile numbers, tenant contacts, and compliance dates, then upload to preview and execute the update.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default BulkContactsUpdate;
